import {
  CloseCircleFilled,
  PaperClipOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Button, Input, message, theme } from 'antd'
import type React from 'react'
import { useRef, useState } from 'react'

import {
  type ComposerAttachment,
  attachmentErrorMessage,
  filesToAttachments,
} from '../attachmentUtils'

export type ComposerProps = {
  attachments: ComposerAttachment[]
  onAttachmentsChange: (next: ComposerAttachment[]) => void
  onSend: (text: string) => void
  onStop: () => void
  status: 'idle' | 'streaming' | 'reconnecting' | 'cancelling' | 'error'
}

export function Composer({
  attachments,
  onAttachmentsChange,
  onSend,
  onStop,
  status,
}: ComposerProps) {
  const { token } = theme.useToken()
  const [text, setText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isBusy =
    status === 'streaming' ||
    status === 'reconnecting' ||
    status === 'cancelling'

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (files.length === 0) return
    const res = await filesToAttachments(files, attachments)
    if (res.error) {
      message.error(attachmentErrorMessage(res.error))
      return
    }
    onAttachmentsChange([...attachments, ...res.attachments])
  }

  function removeAttachment(filename: string) {
    onAttachmentsChange(attachments.filter((a) => a.filename !== filename))
  }

  function handleSend() {
    if (!text.trim()) return
    onSend(text.trim())
    setText('')
  }

  return (
    <Vertical gap={8}>
      {attachments.length > 0 && (
        <Horizontal gap={8} style={{ flexWrap: 'wrap' }}>
          {attachments.map((a) => (
            <div
              key={a.filename}
              style={{ position: 'relative', width: 56, height: 56 }}
            >
              <img
                src={`data:${a.mime_type};base64,${a.content_b64}`}
                alt={a.filename}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 6,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              />
              <Button
                type='text'
                size='small'
                aria-label={`Remove ${a.filename}`}
                icon={
                  <CloseCircleFilled
                    style={{ color: token.colorTextTertiary }}
                  />
                }
                onClick={() => removeAttachment(a.filename)}
                style={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  padding: 0,
                }}
              />
            </div>
          ))}
        </Horizontal>
      )}
      <Horizontal gap={4} verticalCenter>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          multiple
          aria-label='Attach image'
          onChange={handleFileChange}
          disabled={isBusy}
          style={{ display: 'none' }}
        />
        <Button
          icon={<PaperClipOutlined />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          aria-label='Add file'
        />
        <Input.TextArea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          autoSize={{ minRows: 1, maxRows: 6 }}
          placeholder='Ask Gravi…'
          disabled={isBusy}
        />
        {isBusy ? (
          <Button icon={<StopOutlined />} onClick={onStop}>
            Stop
          </Button>
        ) : (
          <Button
            type='primary'
            icon={<SendOutlined />}
            onClick={handleSend}
            disabled={!text.trim()}
          >
            Send
          </Button>
        )}
      </Horizontal>
    </Vertical>
  )
}
